import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { MessageSquare, Send, Save, Check, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const submittedQuotes = [
  { title: 'The Wonders of Coding', myQuote: 18.50, lowest: 18.50, lowestMine: true, recommended: true, moeStatus: 'approved' },
  { title: 'Climate Change & You', myQuote: 16.80, lowest: 16.80, lowestMine: true, recommended: true, moeStatus: 'approved' },
  { title: 'Secondary Science Digest', myQuote: 15.20, lowest: 14.80, lowestMine: false, gap: '+$0.40', recommended: false, moeStatus: 'not_selected' },
  { title: 'Malay Folklore Vol 4', myQuote: 12.40, lowest: 12.40, lowestMine: true, recommended: true, moeStatus: 'approved' },
];

export default function SupplierQuoteResponse() {
  const handleSubmit = () => {
    toast.success('Quote submitted successfully');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
          <MessageSquare className="w-7 h-7 text-[#7A4020]" />
          Quote Response — Open Invitations
        </h1>
        <p className="text-slate-500 mt-1">CDS Cycle 1 2028</p>
      </div>

      <Tabs defaultValue="open">
        <TabsList>
          <TabsTrigger value="open">Open Invitations (12)</TabsTrigger>
          <TabsTrigger value="submitted">Submitted Quotes (68)</TabsTrigger>
          <TabsTrigger value="results">Results</TabsTrigger>
        </TabsList>

        {/* Open Invitations */}
        <TabsContent value="open" className="mt-6 space-y-6">
          {/* Standard Invitation */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-base">"The Wonders of Coding"</CardTitle>
                  <CardDescription>Secondary NF EL | Budget ceiling: $20.00 | Deadline: 2 days</CardDescription>
                </div>
                <Badge variant="outline">Standard Invitation</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="price">Unit price ($)</Label>
                  <Input id="price" type="number" step="0.01" defaultValue="18.50" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="stock">Stock quantity</Label>
                  <Input id="stock" type="number" defaultValue="500" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="delivery">Delivery commitment</Label>
                  <Input id="delivery" defaultValue="By 30 Nov 2027" className="mt-1" />
                </div>
              </div>
              <div className="mt-4">
                <Label htmlFor="notes">Notes</Label>
                <Textarea id="notes" placeholder="Local warehouse stock ready" className="mt-1" />
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={handleSubmit} className="bg-[#7A4020] hover:bg-[#5A3018]" data-testid="submit-quote-btn">
                  <Send className="w-4 h-4 mr-2" />
                  Submit Quote
                </Button>
                <Button variant="outline">
                  <Save className="w-4 h-4 mr-2" />
                  Save Draft
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Low Stock Situation */}
          <Card className="border-amber-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-base">"Tamil Literature Anthology"</CardTitle>
                  <CardDescription>Budget ceiling: $15.00</CardDescription>
                </div>
                <Badge className="bg-amber-100 text-amber-700">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Low Stock
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="p-3 bg-amber-50 rounded-lg mb-4">
                <p className="text-sm text-amber-800">
                  ⚠️ Your declared stock (40 units) may be insufficient for demand across ~156 secondary schools.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Unit price ($)</Label>
                  <Input type="number" step="0.01" defaultValue="14.20" className="mt-1" />
                </div>
                <div>
                  <Label>Stock quantity</Label>
                  <Input type="number" defaultValue="40" className="mt-1" />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea placeholder="Can source add'l 60 units from wholesale — 100 total by Oct 2027" className="mt-1" />
                </div>
              </div>
              <Button className="mt-4 bg-[#7A4020]">Submit Quote</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Submitted Quotes */}
        <TabsContent value="submitted" className="mt-6">
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>My Quote</TableHead>
                    <TableHead>Lowest</TableHead>
                    <TableHead>Gap</TableHead>
                    <TableHead>Recommended?</TableHead>
                    <TableHead>MOE Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submittedQuotes.map((quote, idx) => (
                    <TableRow key={idx} className="table-row-striped">
                      <TableCell className="font-medium">{quote.title}</TableCell>
                      <TableCell>${quote.myQuote.toFixed(2)}</TableCell>
                      <TableCell>
                        ${quote.lowest.toFixed(2)} {quote.lowestMine && '(mine)'}
                      </TableCell>
                      <TableCell>{quote.gap || '—'}</TableCell>
                      <TableCell>
                        {quote.recommended ? (
                          <Badge className="bg-emerald-50 text-emerald-700">✓ Yes</Badge>
                        ) : (
                          <Badge variant="outline">No</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {quote.moeStatus === 'approved' && (
                          <Badge className="bg-emerald-50 text-emerald-700">✓ MOE Approved</Badge>
                        )}
                        {quote.moeStatus === 'not_selected' && (
                          <Badge variant="outline" className="text-slate-500">Not selected</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-sm text-slate-500 mt-4 text-center">Showing 4 of 68 submitted quotes</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Results */}
        <TabsContent value="results" className="mt-6">
          <Card className="bg-slate-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 p-4 bg-white rounded-lg border mb-4">
                <X className="w-5 h-5 text-red-600" />
                <div>
                  <p className="font-medium">"Secondary Science Digest" — Not Selected</p>
                  <p className="text-sm text-slate-500">
                    Your quote was received but another supplier was selected for this title. No PO will be issued for this title.
                  </p>
                </div>
              </div>
              <p className="text-sm text-slate-600">
                Notification: Popular Books selected (higher stock despite lower OpenTrolley price).
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
